/**
 * startTheGame.js
 *
 * Function to start the Impostors game.
 * Randomly selects impostors based on player count,
 * updates their Firestore docs with impostor: true,
 * and writes the impostor IDs to the room document.
 */

import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { getNumberOfImpostors } from "../assets/Details";
import { Lobby, TrustIssuesRoomCollection } from "../../../assets/DatabaseCollections";
import { generateAndStoreColorGrids } from "./colorGridHelpers";

/**
 * Starts the Impostors game.
 * ------------------------------------------------------------
 * 1️⃣ Determines how many impostors there should be
 * 2️⃣ Randomly selects impostors (excluding specific protected users)
 * 3️⃣ Updates Firestore lobby and room docs
 * 4️⃣ Creates color grids for the round
 *
 * @param {string} roomID        - The room ID where the game is being played.
 * @param {Array<Object>} players - Array of player objects from the lobby.
 * @returns {Promise<Array>}     - Updated player list with impostor flags.
 */
export const startImpostorsGame = async (roomID, players) => {
    try {
        if (!players || players.length === 0) {
            console.error("❌ No players found in lobby.");
            return [];
        }

        // Step 1️⃣ : Determine number of impostors
        const totalPlayers      = players.length;
        const numberOfImpostors = Math.floor(getNumberOfImpostors(totalPlayers));

        // Step 2️⃣ : Randomly select impostors — EXCLUDING specific users
        const protectedEmail = "yash301020@gmail.com";

        // Separate protected player (if exists)
        const safePlayers = players.filter(p => p.email !== protectedEmail);
        const protectedPlayers = players.filter(p => p.email === protectedEmail);

        // Shuffle safe players and pick impostors
        const shuffledSafePlayers = [...safePlayers].sort(() => Math.random() - 0.5);
        const impostors = shuffledSafePlayers.slice(0, numberOfImpostors);
        const impostorIDs = impostors.map(p => p.id);

        // Step 3️⃣ : Update impostor flags in Firestore lobby
        for (const impostor of impostors) {
            const impostorRef = doc(db, TrustIssuesRoomCollection, roomID, Lobby, impostor.id);
            await updateDoc(impostorRef, { impostor: true });
        }

        // Step 4️⃣ : Update the room doc with impostor list and stage = 1
        const roomRef = doc(db, TrustIssuesRoomCollection, roomID);
        await updateDoc(roomRef, { impostors: impostorIDs, stage: 1 });

        // Step 5️⃣ : Return updated players array
        const updatedPlayers = players.map(p => ({
            ...p,
            impostor: impostorIDs.includes(p.id)
        }));

        // Step 6️⃣ : Create color grids for this game
        await generateAndStoreColorGrids(roomID, players.length);

        console.log(
            `[Game Start] Successfully started Impostors game with ${numberOfImpostors} impostors:`,
            impostorIDs
        );

        // ✅ Log the excluded user for transparency
        if (protectedPlayers.length > 0)
            console.log(`[Game Start] Skipped assigning impostor role to: ${protectedPlayers[0].email}`);

        return updatedPlayers;
    } catch (error) {
        console.error("[Game Start] ❌ Failed to start the game:", error);
        return [];
    }
};